/**
 * チャット通知の送信先を管理するエンドポイント (v3.2.21+)。
 *
 * Firestore コレクション: `chat_endpoints/{endpointId}`
 *
 * 設計:
 *  - 種類 (kind) は Google Chat と Chatwork の 2 つ (2026-10-26 に Chatwork を追加)。
 *    kind が無い既存ドキュメントは Google Chat として扱う。
 *  - Google Chat: 業務通知テンプレ (BusinessNotificationTemplate.chat.endpointId) や
 *    管理者通知・経理担当通知が ID 参照する。webhookUrl へ POST して送る。
 *  - Chatwork: 経理担当通知 (settings/sales.accountingChatworkEndpointId) が ID 参照する。
 *    送信は Chatwork API (roomId + シークレット CHATWORK_API_TOKEN) で行う。
 *    webhookUrl は記録用で、送信には使わない (Chatwork に受信用 Webhook は無いため)。
 *  - 複数登録可能 (例: 「社内一般」「経理チーム」など、用途別に分ける)
 *  - 値は Firestore に平文で保存される。admins 限定の Firestore rule で守る前提
 *  - 既存の sendGoogleChatNotification (固定 Secret から URL を引く「内部システムアラート」用) とは別経路
 */
import type { IsoOrTimestamp } from './common';

export type ChatEndpointKind = 'google_chat' | 'chatwork';

export const CHAT_ENDPOINT_KIND_LABELS: Record<ChatEndpointKind, string> = {
  google_chat: 'Google Chat',
  chatwork: 'Chatwork',
};

export interface ChatEndpoint {
  /** 種類。未設定 (既存データ) は 'google_chat'。 */
  kind?: ChatEndpointKind;
  /** 表示ラベル (例: "社内一般", "経理チーム") */
  name: string;
  /**
   * Google Chat: incoming webhook URL (必須)。
   * Chatwork: 記録用の Webhook URL (任意・送信には使わない)。未入力は ''。
   */
  webhookUrl: string;
  /** Chatwork のルーム ID (Chatwork のみ必須)。 */
  roomId?: string | null;
  /** 任意の説明 (用途メモ) */
  description?: string | null;
  /** false なら送信スキップ (削除しないで一時停止) */
  isActive: boolean;
  createdAt: IsoOrTimestamp;
  createdBy: string;
  updatedAt: IsoOrTimestamp;
}

/** kind 未設定の既存データを Google Chat として扱う。 */
export function chatEndpointKind(
  ep: Pick<ChatEndpoint, 'kind'> | null | undefined,
): ChatEndpointKind {
  return ep?.kind === 'chatwork' ? 'chatwork' : 'google_chat';
}
